"use client";

import { useCallback, useRef, useState } from "react";
import { animate, useMotionValue } from "framer-motion";

// The deck's position, shared by the deck that a finger drives and the one the
// arrows do.
//
// `p` is deliberately unbounded — it keeps counting past the last card, and the
// wrap in depthOf() maps it back onto the deck. That is what lets someone keep
// going in one direction forever, with no dead end at either end.
//
// The committed position lives in a ref (a drag reads it every frame, and it
// must not re-render); `index` is the same value wrapped into 0…count-1, kept in
// state so the controls can re-render.
const SNAP = { type: "spring", stiffness: 260, damping: 30 };

export default function useDeckPosition(count) {
  const p = useMotionValue(0);
  const positionRef = useRef(0);
  const [index, setIndex] = useState(0);

  const goTo = useCallback(
    (next) => {
      positionRef.current = next;
      setIndex(((next % count) + count) % count);
      animate(p, next, SNAP);
    },
    [count, p],
  );

  // Controls address a card by its index, but `p` counts without wrapping — so
  // pick whichever equivalent position is nearest, and the deck takes the short
  // way round instead of unwinding through every card in between.
  const goToIndex = useCallback(
    (target) => {
      const base = Math.round((positionRef.current - target) / count) * count;
      goTo(base + target);
    },
    [count, goTo],
  );

  const step = useCallback(
    (delta) => goTo(positionRef.current + delta),
    [goTo],
  );

  return { p, index, positionRef, goTo, goToIndex, step };
}
